import { type VariantProps, cva } from 'class-variance-authority'
import { Slot as SlotPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md cursor-pointer transition-colors disabled:pointer-events-none [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring focus-visible:ring-[3px] aria-invalid:ring-red-300/20 dark:aria-invalid:ring-red-300/40 aria-invalid:border-red-300 active:outline active:outline-1 active:outline-solid [&_svg:not([class*='size-'])]:size-4 ",
  {
    variants: {
      variant: {
        default:
          'bg-blue-200 text-white-100 hover:bg-blue-300 active:outline-blue-300 active:hover:bg-blue-200 disabled:text-grey-100 disabled:bg-grey-500',
        destructive:
          'bg-red-300 text-white-100 hover:bg-red-400 active:outline-red-400 active:bg-red-300 disabled:text-grey-100 disabled:bg-grey-500',
        outline:
          'border hover:border-grey-500 active:border-black-200 active:outline-none disabled:text-grey-500 [&_svg]:text-grey-600',
        ghost:
          'hover:bg-grey-300 [&_svg]:text-grey-600 hover:[&_svg]:text-grey-500 disabled:text-grey-400 disabled:bg-grey-200 active:outline-none active:bg-transparent '
      },
      size: {
        default: 'h-9 p-2.5',
        sm: "h-7.5 p-2 [&_svg:not([class*='size-'])]:size-3.5 gap-1.5 text-sm",
        lg: 'h-10 p-3',
        icon: 'size-9',
        'icon-sm': "size-7.5 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-lg': 'size-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? SlotPrimitive.Slot : 'button'

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
